import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../Core/Notifier";
import { API_URL } from "../../config";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", greenBd: "#a8d5ac",
  redTx: "#9b2c2c", redBg: "#fef2f2",
  peach: "#ffebd3", inputBg: "#f8f7f5",
  blueBg: "#eff6ff", blueTx: "#1e40af",
};

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const get_ = (p: string) => fetch(`${API_URL}${p}`, { headers: hdrs() }).then(r => r.json());
const del_ = (p: string) => fetch(`${API_URL}${p}`, { method: "DELETE", headers: hdrs() }).then(r => r.json());

const VendorsListPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await get_(`/vendors/manage${qs}`);
    if (res.code === 200) setVendors(res.data ?? []);
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchVendors, 300);
    return () => clearTimeout(timer);
  }, [fetchVendors]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await del_(`/vendors/manage/${deleteTarget.id}`);
    if (res.code === 200 && res.status) { showMessage("Vendor deleted"); fetchVendors(); }
    else showErrorMessage(res.message || "Failed to delete");
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const stats = useMemo(() => {
    const total = vendors.length;
    const active = vendors.filter(v => v.status === "active").length;
    const withPortal = vendors.filter(v => v.has_credentials).length;
    const totalItems = vendors.reduce((sum: number, v: any) => sum + (v.item_count || 0), 0);
    return { total, active, withPortal, totalItems };
  }, [vendors]);

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>Vendors</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>Manage vendor profiles, credentials, items, and assignments</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate("/vendors/create")}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
          Add Vendor
        </Button>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" }, gap: 2, mb: 3 }}>
        {[
          { label: "Total Vendors", value: stats.total, color: T.navy, bg: `${T.navy}08` },
          { label: "Active", value: stats.active, color: T.greenTx, bg: T.greenBg },
          { label: "Portal Access", value: stats.withPortal, color: T.blueTx, bg: T.blueBg },
          { label: "Total Items", value: stats.totalItems, color: T.gold, bg: `${T.gold}15` },
        ].map(s => (
          <Paper key={s.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, bgcolor: T.white }}>
            <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: s.color, mt: 0.5 }}>{s.value}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Search + Filter */}
      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, maxWidth: 360 }} />
        <TextField size="small" select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 150 }}>
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Box>

      {/* Vendor List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
      ) : vendors.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, mb: 1.5 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.navy, mb: 0.5 }}>No vendors found</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mb: 2 }}>Get started by adding your first vendor.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/vendors/create")}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", "&:hover": { bgcolor: "#1a3550" } }}>
            Add Vendor
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {vendors.map((v: any) => {
            const hasCredentials = v.has_credentials;
            const itemCount = v.item_count || 0;
            const assignCount = v.assignment_count || 0;
            return (
              <Paper key={v.id} elevation={0} sx={{
                borderRadius: "12px", border: `1.5px solid ${T.line}`, bgcolor: T.white,
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)", overflow: "hidden", transition: "all 0.15s",
                "&:hover": { borderColor: `${T.gold}60`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
                  {/* Avatar */}
                  <Box sx={{
                    width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                    bgcolor: `${T.gold}12`, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <StorefrontOutlinedIcon sx={{ fontSize: 22, color: T.gold }} />
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }} noWrap>{v.name}</Typography>
                      <Chip label={v.status} size="small" sx={{
                        height: 18, fontSize: 9, fontWeight: 600,
                        bgcolor: v.status === "active" ? T.greenBg : T.redBg,
                        color: v.status === "active" ? T.greenTx : T.redTx,
                      }} />
                      {hasCredentials && (
                        <Tooltip title="Has portal access">
                          <Chip icon={<VpnKeyOutlinedIcon sx={{ fontSize: 10, color: `${T.greenTx} !important` }} />} label="Portal" size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.greenBg, color: T.greenTx }} />
                        </Tooltip>
                      )}
                      {itemCount > 0 && (
                        <Chip icon={<InventoryOutlinedIcon sx={{ fontSize: 10, color: `${T.gold} !important` }} />} label={`${itemCount} items`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                      )}
                      {assignCount > 0 && (
                        <Chip icon={<AssignmentOutlinedIcon sx={{ fontSize: 10, color: `${T.blueTx} !important` }} />} label={`${assignCount} assignments`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: T.blueBg, color: T.blueTx }} />
                      )}
                    </Box>
                    {/* Service category chips */}
                    {(() => {
                      const cats: any[] = v.service_categories || [];
                      const maxShow = 4;
                      const visible = cats.slice(0, maxShow);
                      const extra = cats.length - maxShow;
                      return cats.length > 0 ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
                          {visible.map((c: any) => (
                            <Chip key={c.id} label={c.name} size="small" sx={{
                              height: 20, fontSize: 10, fontWeight: 500,
                              bgcolor: `${T.navy}08`, color: T.navy,
                              border: `1px solid ${T.navy}15`,
                            }} />
                          ))}
                          {extra > 0 && (
                            <Tooltip title={cats.slice(maxShow).map((c: any) => c.name).join(", ")}>
                              <Chip label={`+${extra} more`} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                            </Tooltip>
                          )}
                        </Box>
                      ) : null;
                    })()}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      {v.company_name && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><BusinessOutlinedIcon sx={{ fontSize: 11 }} /> {v.company_name}</Typography>}
                      {v.phone && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><PhoneOutlinedIcon sx={{ fontSize: 11 }} /> {v.phone}</Typography>}
                      {v.email && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><EmailOutlinedIcon sx={{ fontSize: 11 }} /> {v.email}</Typography>}
                      {v.city && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><LocationOnOutlinedIcon sx={{ fontSize: 11 }} /> {v.city}, {v.province}</Typography>}
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Tooltip title="View Profile">
                    <IconButton size="small" onClick={() => navigate(`/vendors/profile/${v.id}`)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                      <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => navigate(`/vendors/edit/${v.id}`)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => setDeleteTarget({ id: v.id, name: v.name })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}><WarningAmberIcon color="error" /> Delete vendor?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={isDeleting} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "Deleting..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VendorsListPage;
